import { useEffect, useState } from "react";
import api, { shortDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MessageSquarePlus, Trash2, Send } from "lucide-react";
import { toast } from "sonner";

export function SalesComments({ customerId, month }) {
  const { user } = useAuth();
  const canPost = user && ["super_admin", "management", "general_manager"].includes(user.role);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/sales-comments", { params: { customer_id: customerId } }).then(r => setComments(r.data));
  useEffect(() => { if (customerId) load(); }, [customerId]);

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post("/sales-comments", { customer_id: customerId, month, text: text.trim() });
      setText("");
      toast.success("Comment posted");
      load();
    } catch { toast.error("Failed to post"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!confirm("Delete this comment?")) return;
    await api.delete(`/sales-comments/${id}`);
    load();
  };

  const shown = month ? comments.filter(c => c.month === month) : comments;

  return (
    <div className="mt-4 border-t border-slate-100 pt-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-slate-500">
        <MessageSquarePlus className="w-3.5 h-3.5" /> Manager Comments {month && <span className="text-slate-400 normal-case tracking-normal">• {month}</span>}
      </div>
      <div className="mt-3 space-y-2">
        {shown.length === 0 && <p className="text-xs text-slate-400 italic">No comments yet.</p>}
        {shown.map(c => (
          <div key={c.id} className="bg-slate-50 rounded-lg p-3 text-sm" data-testid={`comment-${c.id}`}>
            <div className="flex justify-between items-start gap-2">
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">
                  {c.author_name} <span className="text-slate-400">• {c.month} • {shortDate(c.created_at)}</span>
                </div>
                <div className="mt-1 text-slate-700 whitespace-pre-wrap">{c.text}</div>
              </div>
              {canPost && (
                <button onClick={() => del(c.id)} className="text-slate-400 hover:text-red-600 shrink-0" data-testid={`comment-delete-${c.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      {canPost && (
        <div className="mt-3 flex gap-2">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={`Leave context for ${month || "this store"}...`}
            className="flex-1 h-10 px-3 border border-slate-300 rounded-lg text-sm"
            data-testid="sales-comment-input"
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          />
          <button onClick={submit} disabled={busy || !text.trim()} className="mlily-btn h-10 px-4 rounded-lg font-semibold text-sm flex items-center gap-1 disabled:opacity-50" data-testid="post-comment-btn">
            <Send className="w-3.5 h-3.5" /> Post
          </button>
        </div>
      )}
    </div>
  );
}
